import { useEffect, useMemo } from 'react';
import {
  Filters,
  SchemaFilterType,
  SelectItemProps,
  useFilters,
  useGetList,
  useQueryState,
} from '@ui/src';
import {
  useLazyGetCustomerDefaultMarginQuery,
  useLazyGetCustomMarginsQuery,
} from '../../../customers/store/services/customers';
import {
  allMarginListFiltersSchema,
  allMarginListSchema,
  marginListFiltersSchema,
  marginListSchema,
} from '../variables/margins';
import { RateSource } from '../../types';
import { CustomMargin } from '../types';
import { PSPDetails } from '../../../../../../../store/types';
import formatMarginsData from '../helpers/format-margins-list';

const useCustomMargins = ({
  rateSources,
  currencies,
  defaultMargin,
  tariffId,
  allMarginsVisible,
  customerId,
  settings,
  customMargins,
  setSelectedCurrency,
  selectedCurrency,
}: {
  rateSources?: RateSource[];
  currencies?: SelectItemProps[];
  defaultMargin?: CustomMargin;
  tariffId?: string;
  allMarginsVisible?: boolean;
  customerId?: string;
  settings?: PSPDetails[];
  customMargins?: CustomMargin[];
  setSelectedCurrency: (data: string) => void;
  selectedCurrency?: string;
}) => {
  const [
    getCustomMargins,
    { isLoading: marginsIsLoading, isFetching: marginsIsFetching },
  ] = useLazyGetCustomMarginsQuery();
  const [getDefaultMargin] = useLazyGetCustomerDefaultMarginQuery();
  const { filters, onFilterChange } = useQueryState(marginListSchema);
  const {
    filters: allMarginFilters,
    onFilterChange: allMarginsOnFilterChange,
  } = useQueryState(allMarginListSchema);

  const { onFilter, errorList } = useGetList<any>({
    getList: getCustomMargins,
    onFilterChange,
    onSortChange: () => {},
    schema: marginListSchema,
  });

  const {
    onFilter: allMarginsOnFilter,
    errorList: allMarginsErrorsList,
    setPage,
  } = useGetList<any>({
    getList: getCustomMargins,
    onFilterChange: allMarginsOnFilterChange,
    onSortChange: () => {},
    schema: allMarginListSchema,
  });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: marginListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const {
    filtersComponent: allMarginsFitlersComponent,
    setValues: allMarginsSetValues,
    handleSubmit: allMarginsHandleSubmit,
  } = useFilters({
    filtersSchema: allMarginListFiltersSchema,
    filters: allMarginFilters,
    onFilterChange: allMarginsOnFilterChange,
  });

  const formattedMarginsData = useMemo(() => {
    const data = formatMarginsData(customMargins as CustomMargin[]);

    return data;
  }, [customMargins, filters]);

  const onAllMarginsCurrencyFilter = (currencyFromId: string) => {
    setSelectedCurrency(currencyFromId);

    allMarginsOnFilter({
      ...allMarginFilters,
      currencyFromId: {
        value: [currencyFromId],
        type: SchemaFilterType.STRING,
      },
      organizationId: {
        value: [customerId as string],
        type: SchemaFilterType.STRING,
      },
      tariffId: {
        value: [tariffId as string],
        type: SchemaFilterType.STRING,
      },
    });
  };

  const onMarginsFilterChange = (values: Filters) => {
    const currentFilters: Filters = {
      ...values,
      organizationId: {
        value: [customerId as string],
        type: SchemaFilterType.STRING,
      },
      createType: {
        value: ['custom'],
        type: SchemaFilterType.STRING,
      },
      take: {
        value: ['6'],
        type: SchemaFilterType.STRING,
      },
    };

    onFilter(currentFilters);
  };

  const onAllMarginsFilterChange = (values: Filters) => {
    const currentFilters: Filters = {
      ...values,
      currencyFromId: {
        value: [selectedCurrency as string],
        type: SchemaFilterType.STRING,
      },
      organizationId: {
        value: [customerId as string],
        type: SchemaFilterType.STRING,
      },
      tariffId: {
        value: [tariffId as string],
        type: SchemaFilterType.STRING,
      },
      take: {
        value: ['20'],
        type: SchemaFilterType.STRING,
      },
    };

    allMarginsOnFilter(currentFilters);
  };

  useEffect(() => {
    if (rateSources && currencies) {
      setValues(
        'rateSourceId',
        rateSources?.map((item) => ({ name: item.name, value: item.id })),
      );

      allMarginsSetValues(
        'rateSourceId',
        rateSources?.map((item) => ({ name: item.name, value: item.id })),
      );
      allMarginsSetValues('currencyToId', currencies);
    }
  }, [rateSources, currencies]);

  useEffect(() => {
    if (!defaultMargin) {
      getDefaultMargin();
    }
  }, [defaultMargin]);

  useEffect(() => {
    if (tariffId && !allMarginsVisible) {
      const currentFilters: Filters = {
        organizationId: {
          value: [customerId as string],
          type: SchemaFilterType.STRING,
        },
        createType: {
          value: ['custom'],
          type: SchemaFilterType.STRING,
        },
        take: {
          value: ['6'],
          type: SchemaFilterType.STRING,
        },
      };

      onFilter(currentFilters);
    }
  }, [tariffId, allMarginsVisible]);

  useEffect(() => {
    if (allMarginsVisible && currencies && tariffId && settings) {
      const baseRateSource = settings?.find(
        (item) => item.key === 'EXCHANGE-RATE-SOURCE',
      );

      const currentFilters: Filters = {
        currencyFromId: {
          value: [currencies[0].value as string],
          type: SchemaFilterType.STRING,
        },
        organizationId: {
          value: [customerId as string],
          type: SchemaFilterType.STRING,
        },
        tariffId: {
          value: [tariffId],
          type: SchemaFilterType.STRING,
        },
        take: {
          value: ['20'],
          type: SchemaFilterType.STRING,
        },
        rateSourceId: {
          value: [baseRateSource?.data?.value as string],
          type: SchemaFilterType.STRING,
        },
      };

      allMarginsOnFilter(currentFilters);
    }
  }, [currencies, allMarginsVisible, settings]);

  return {
    marginsIsLoading,
    onAllMarginsCurrencyFilter,
    formattedMarginsData,
    errorList,
    filters,
    onMarginsFilterChange,
    filtersComponent,
    handleSubmit,
    marginsIsFetching,
    allMarginsErrorsList,
    allMarginFilters,
    onAllMarginsFilterChange,
    allMarginsFitlersComponent,
    allMarginsHandleSubmit,
    setPage,
  };
};

export default useCustomMargins;
